<!-- PatientPortal_MiscellaneousScreen.vue -->
<script lang="ts" setup>
import { computed, nextTick, onBeforeMount, reactive, ref, watch } from "vue";
import { IMiscScreenSectionInfo } from "../../../lib/Api/Patient";
import * as choiceApi from '../../../lib/ChoiceApi';
import ChronicCareManagement from "./ChronicCareManagement.vue"
import NewYorkPreschoolBilling from "./NewYorkPreschoolBilling.vue";
import AppointmentReminders from "./AppointmentReminders.vue";
import PaymentReceiptPreference from "./PaymentReceiptPreference.vue"
import OptimizeRx from './OptimizeRx.vue'
import PrescriptionEducation from "./PrescriptionEducation.vue"
import HealthInformationExchange from "./HealthInformationExchange.vue";
import MDRevolutionStatus from "./MDRevolutionStatus.vue";
import CkccStatus from "./CkccStatus.vue";
import SureScriptsRecordLocatorConsent from "./SureScriptsRecordLocatorConsent.vue";
import MessageBadge from "../../../components/Messages/MessageBadge.vue";
import { DisplayStyle, IconTypes } from "../../../Enums/UiEnums";
import { DatabaseIdTypes, FeatureType, PrivilegeType } from "../../../Enums/ChoiceEnums"
import FoldingPanel from "../../../components/FoldingPanel.vue";
import { IStatusResponse } from "../../../lib/ChoiceApi";
import { popAlert } from "../../../lib/Notification";
import { AppScreenNames, MiscScreenSectionId, MessageChannelName } from "../../../Enums/ChoiceEnums";
import ChoiceStateMessageSink from "../../../components/ChoiceStateMessageSink.vue";
import FeatureConditional from "../../Conditionals/FeatureConditional.vue";
import { IPatientDecodeValue } from "../../../lib/Models/Patient/IPatientDecodeValue";
import { IPatientSureScriptsInfo } from "../../../lib/Models/Patient/IPatientSureScriptsInfo";
import FlashMessage from "../../../components/Messages/FlashMessage.vue";
import Icon from "../../../components/Icon.vue";
import { timeout } from "../../../lib/ChoiceUtils";
import { playErrorSound } from "../../../lib/Sound";

    const props = defineProps<{
        id?: number,
        idType?: DatabaseIdTypes.patient_id | DatabaseIdTypes.entity_id
    }>();

    const emits = defineEmits<{
        dataChanged: [dataHasChanged:boolean],
        allowSave: [allow:boolean],
        patientId: [patientId?: number]
    }>();

    defineExpose({save, requestStateEvents});

    const compData = reactive<{
        patientId?: number,
        sectionInfo?: IMiscScreenSectionInfo[],
        decodeValues?: IPatientDecodeValue,
        sureScriptsInfo?: IPatientSureScriptsInfo,
        allowSaveData?: boolean,
        saving?: boolean,
        initial: {
            decodeValuesJSON?: string,
            sureScriptsConsentGiven?: boolean
        }
    }>({
        initial: { }
    });

    const flashSaved = ref<InstanceType<typeof FlashMessage>>();
    const flashNothingToSave = ref<InstanceType<typeof FlashMessage>>();
    
    // Has the screen data changed
    function emitScreenDataHasChangedValue(hasChanged:boolean)
    {
        emits("dataChanged", hasChanged);
    }

    onBeforeMount(async () => {
        emitScreenDataHasChangedValue(false);

        choiceApi.patient.getMiscScreenSectionsConfig()
            .then(resp => {
                compData.sectionInfo = resp;
            });

        loadPatientData();

        choiceApi.user.screenPrivilege(AppScreenNames.PatientPortal_Miscellaneous).then(resp =>{
            compData.allowSaveData = resp > PrivilegeType.Read;
            emits("allowSave", compData.allowSaveData);
        });
    });    

    async function loadPatientData()
    {
        compData.decodeValues = undefined;
        compData.sureScriptsInfo = undefined;

        switch(props.idType)
        {
            case DatabaseIdTypes.entity_id:
                if (props.id)
                {
                    compData.patientId = (await choiceApi.patient.getPatientHeaderBasicInfo(props.id, props.idType))?.patient_id;
                }
                break;

            case DatabaseIdTypes.patient_id:
                compData.patientId = props.id;
                break;

            default:
                compData.patientId = undefined;
                await popAlert(`Unexpected idType: ${props.idType}`);
                return;
        }

        // Indicate the current patient ID
        emits("patientId", compData.patientId);

        // Make sure patient ID is valid
        if (!compData.patientId)
            return;

        const decodeValueApiPromise = choiceApi.patient.getPatientDecodeValues(compData.patientId)
                    .then(resp => { 
                        compData.decodeValues = resp; 
                        compData.initial.decodeValuesJSON = JSON.stringify(compData.decodeValues);
                    });
        const surescriptsApiPromise = choiceApi.patient.getPatientSureScriptsInfo(compData.patientId)
                    .then(resp => { 
                        compData.sureScriptsInfo = resp;
                        compData.initial.sureScriptsConsentGiven = compData.sureScriptsInfo?.consentGiven;
                    });      
                    

        Promise.all([decodeValueApiPromise, surescriptsApiPromise]).then(()=>{
            if (compData.decodeValues && compData.sureScriptsInfo)
            {                
                emits("allowSave", true);;
            }
        });
                    
    }


    const decodeValuesChanged = computed<boolean>(() => compData.initial.decodeValuesJSON !== JSON.stringify(compData.decodeValues));
    const sureScriptsConsentStateChanged = computed<boolean>(() => undefined !== compData.sureScriptsInfo?.consentGiven && (compData.sureScriptsInfo?.consentGiven ?? false) !== (compData.initial.sureScriptsConsentGiven ?? false));
    const dataChanged = computed<boolean>(() => decodeValuesChanged.value || sureScriptsConsentStateChanged.value);

    // If the patient ID changes load new patient data
    watch([() => props.id, () => props.idType], (_newVal, _oldVal)=>{
        loadPatientData();

        emitScreenDataHasChangedValue(false);
    });


    watch(dataChanged, (newVal, _oldVal)=>{
        emitScreenDataHasChangedValue(newVal);
    });

    // This method is called externally 
    async function save(showPopupWhenNoChanges:boolean=true): Promise<void>
    {
        // Prevent re-entry since legacy save button doesn't always respect our attempt to disable it
        //  The falling method seems to have a timeout that re-enables the save button
        if (compData.saving)
            return;
        compData.saving = true;

        // Try to defeat the calling function timeout that re-enables the save button
        timeout(500).then(() =>{
            if (compData.saving)
            {
                emits("allowSave", false)
            }
        });

        await saveContinued(showPopupWhenNoChanges);
        
        compData.saving = false;
    }

    function requestStateEvents()
    {
        // Wait a moment before sending the new state value
        nextTick(() => {
            emits("allowSave", compData.allowSaveData ?? false);
        });
    }

    // This method will save the data without checking if we're currently saving
    async function saveContinued(showPopupWhenNoChanges:boolean=true): Promise<void>
    {
        if (!dataChanged.value)
        {
            if (showPopupWhenNoChanges)
            {
                // Disable the "Save" button until the popup is closed.
                if (compData.allowSaveData)
                    emits("allowSave", false);

                playErrorSound();
                flashNothingToSave.value?.show();
                emits("allowSave", compData.allowSaveData ?? false);
            }
            return;
        }

        if (decodeValuesChanged.value && compData.decodeValues)
        {
            const resp = await choiceApi.patient.savePatientDecodeValues(compData.decodeValues);
            const errorSaving = await showError(resp, "Save patient decode values");
            if (!errorSaving)
            {
                compData.initial.decodeValuesJSON = JSON.stringify(compData.decodeValues);
            }
        }
        
        // Has consent state changed?
        if (sureScriptsConsentStateChanged.value && undefined != compData.sureScriptsInfo?.consentGiven)
        {
            const resp = await choiceApi.patient.updatePatientSureScriptsConsent(compData.patientId ?? 0, compData.sureScriptsInfo?.consentGiven);
            if (!resp)
            {
                await popAlert("Problem setting SureScripts consent");
            }
            else
            {
                compData.sureScriptsInfo = resp;
                compData.initial.sureScriptsConsentGiven = compData.sureScriptsInfo?.consentGiven;
            }
        }
        flashSaved.value?.show();
    }

    async function showError(resp: IStatusResponse|undefined, context: string): Promise<boolean> {
        if (!resp) 
        {
            await popAlert(`${context}: Failed to get response from server`);
            return true;
        }

        if (resp.successful) 
            return false;

        const msg = resp.msg ?? "Error not specified";
        await popAlert(`${context}: ${msg}`);
        return true;
    }
</script>

<template>
    <template v-for="section in compData.sectionInfo?.filter(section => section.isActive)">
        <template v-if="compData.decodeValues">    
            <FoldingPanel v-if="MiscScreenSectionId.NYPreschoolBilling == section.section">
                <template #title>NY Preschool Billing</template>
                <template #body>
                    <NewYorkPreschoolBilling :patientData="compData.decodeValues" />
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.ChronicCareManagement == section.section">
                <template #title>Chronic Care Management</template>
                <template #body>
                    <ChronicCareManagement v-model:patientData="compData.decodeValues" />
                </template>
            </FoldingPanel>
    
            <FoldingPanel v-if="MiscScreenSectionId.AppointmentReminders == section.section">
                <template #title>Appointment Reminders</template>
                <template #body>
                    <AppointmentReminders :patientData="compData.decodeValues" />
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.OptimizeRx == section.section">
                <template #title>OptimizeRx</template>
                <template #body>
                    <OptimizeRx :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.PrescriptionEducation == section.section">
                <template #title>Prescription Education</template>
                <template #body>
                    <PrescriptionEducation :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>
            
            <FoldingPanel v-if="MiscScreenSectionId.HealthInformationExchange == section.section">
                <template #title>Health Information Exchange</template>
                <template #body>
                    <HealthInformationExchange :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.MDRevolutionStatus == section.section">
                <template #title>MDRevolution Status</template>
                <template #body>
                    <MDRevolutionStatus :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.CKCCStatus == section.section">
                <template #title>CKCC Status</template>
                <template #body>
                    <CkccStatus :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>

            <FoldingPanel v-if="MiscScreenSectionId.PaymentReceiptPreference == section.section">
                <template #title>Payment Receipt Preference</template>
                <template #body>
                    <PaymentReceiptPreference :patientData="compData.decodeValues"/>
                </template>
            </FoldingPanel>    
        </template>

        <FeatureConditional :feature="FeatureType.SureScriptsRecordLocator" v-if="MiscScreenSectionId.RecordLocatorConsent == section.section && compData.sureScriptsInfo">
            <FoldingPanel>
                <template #title>Record Locator Consent</template>
                <template #body>
                    <template v-if="compData.sureScriptsInfo">
                        <SureScriptsRecordLocatorConsent :sureScriptsInfo="compData.sureScriptsInfo" />
                    </template>
                    <template v-else>
                        <MessageBadge :displayStyle="DisplayStyle.Danger">Patient SureScripts info not available</MessageBadge>
                    </template>
                </template>
            </FoldingPanel>
        </FeatureConditional>
    </template>

    <ChoiceStateMessageSink :channel-name="MessageChannelName.PatientPortal_MiscellaneousScreen" @message="save"/>

    <FlashMessage ref="flashSaved" :displayStyle="DisplayStyle.Success">
        <Icon :icon="IconTypes.CheckMark" :displayStyle="DisplayStyle.Success"/> Saved
    </FlashMessage>        

    <FlashMessage ref="flashNothingToSave" :displayStyle="DisplayStyle.Warning">
        <Icon :icon="IconTypes.Warning" :displayStyle="DisplayStyle.Warning"/>
        There are no changes to save
    </FlashMessage>

</template>
